import { useState, type FC } from "react";
import TextButton from "../../atoms/buttons/TextButton";
import { UserIcon } from "../../atoms/icons/UserIcon";
import styled from "styled-components";
import { Col, Row } from "react-grid-system";
import Subtitle from "../../atoms/typography/Subtitle";
import { theme } from "../../../theme";
import IconButton from "../../atoms/buttons/IconButton";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import { AnimatePresence, motion } from "framer-motion";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Subheading from "../../atoms/typography/Subheading";
import Body from "../../atoms/typography/Body";
import BodySmall from "../../atoms/typography/BodySmall";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { FixIconSmall } from "../../atoms/icons/FixIcon";
import { SendIcon } from "../../atoms/icons/SendIcon";
import DonutStatSection from "../../molecules/assessment/DonutStatSection";
import OpenInvoicesList from "../../molecules/assessment/OpenInvoicesList";
import type { InvoiceProcessStatus } from "../../../models/InvoiceStatusModel";
import type { InvoiceSortBy } from "../../../models/InvoiceSortBy";
import type { SortDirection } from "../../../models/SortDirection";
import { useTranslation } from "react-i18next";
import CreditScoreOverviewSmall from "../../molecules/assessment/CreditScoreOverviewSmall";
import { useClient } from "../../../graphql/hooks/useClient";
import { useClientAssessmentInvoices } from "../../../graphql/hooks/invoices/useClientAssessmentInvoices";
import {
  formatAmountWithCurrency,
  formatPrice,
} from "../../../utils/formatters";
import Tooltip from "../../molecules/Tooltip";
import { UserAlertIcon } from "../../atoms/icons/UserAlertIcon";
import { useClientStatistics } from "../../../graphql/hooks/client/useClientStatistics";
import ClientAddresses from "../../molecules/client/ClientAddresses";
import ClientOrgInfo from "../../molecules/client/ClientOrgInfo";
import SendInvoiceMessageToClientButton from "../../molecules/invoices/SendInvoiceMessageToClientButton";
import { getAmlStatusBadge } from "../../molecules/ClientCardHeader";

interface Props {
  clientIdentifier: string;
}

const OverlayContent = styled(motion.div)`
  padding: 24px;
  background-color: white;
  border-radius: 4px;
  max-height: 90vh;
  width: 80vw;
  max-width: 1400px;

  position: fixed;
  top: 0;
  right: 0;
  box-shadow: ${theme.shadow.cardShadow};
  z-index: 100;
  overflow-y: scroll;
`;

const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.1);
  z-index: 100;
`;

const FlexedDiv = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const OutlinedContainer = styled.div`
  border: 1px solid ${theme.colors.grey05};
  border-radius: 12px;
  padding: 16px;
`;

const StatContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 8px;
  background-color: ${theme.colors.grey02};
`;

export const RecipientDetailsOverlay: FC<Props> = ({ clientIdentifier }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const { client } = useClient(clientIdentifier);
  const { clientStatistics } = useClientStatistics(clientIdentifier);
  const overdueByCurrency =
    clientStatistics?.invoicesStatistics?.maturityStructure
      ?.overdueByCurrency ?? [];

  const [openInvoicesPage, setOpenInvoicesPage] = useState(0);
  const [openInvoicesStatuses, setOpenInvoicesStatuses] = useState<
    Array<string> | undefined
  >(undefined);
  const [openInvoicesSearch, setOpenInvoicesSearch] = useState("");
  const [openInvoicesSortBy, setOpenInvoicesSortBy] =
    useState<InvoiceSortBy>("CREATED_AT");
  const [openInvoicesSortDirection, setOpenInvoicesSortDirection] =
    useState<SortDirection>("DESC");
  const INVOICES_PER_PAGE = 5;

  const { invoices: openInvoicesData } = useClientAssessmentInvoices(
    clientIdentifier,
    {
      sortBy: openInvoicesSortBy,
      sortDirection: openInvoicesSortDirection,
      invoiceStatuses:
        openInvoicesStatuses && openInvoicesStatuses.length > 0
          ? (openInvoicesStatuses as InvoiceProcessStatus[])
          : ["VALIDATION", "ASSESSMENT"],
      skip: openInvoicesPage * INVOICES_PER_PAGE,
      take: INVOICES_PER_PAGE,
      searchTerm: openInvoicesSearch || undefined,
    },
  );

  const openInvoices = (openInvoicesData?.items ?? []).map((inv) => ({
    id: inv.id,
    status: (inv?.state?.processStatus ?? "UNKNOWN") as InvoiceProcessStatus,
    invoiceNumber: inv.invoiceNumber,
    recipientName: inv.invoiceRecipient?.name ?? "-",
    invoiceDate: new Date(inv.invoiceDate ?? ""),
    dueDate: new Date(inv.dueDate ?? ""),
    amount: inv.totalAmountWithVat ?? 0,
  }));

  const recipientLimit = clientStatistics?.limits?.recipientLimit;
  const recipientTotal = recipientLimit?.used ?? 0;
  const recipientUsed = recipientLimit?.limit ?? 0;
  const recipientRemaining = Math.max(recipientTotal - recipientUsed, 0);

  const hasImportantNotes = false;

  return (
    <>
      <TextButton
        title={t("common.recipient")}
        icon={
          hasImportantNotes ? (
            <Tooltip
              text={t("invoiceAssessment.clientHasImportantNotification")}
            >
              <UserAlertIcon color={theme.colors.warning} />
            </Tooltip>
          ) : (
            <UserIcon color={theme.colors.primary} />
          )
        }
        onClick={() => setOpen(true)}
        color={theme.colors.primary}
      />

      <AnimatePresence>
        {open && (
          <>
            <Backdrop
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />

            <OverlayContent
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Row nogutter align="center" justify="between">
                <Subtitle color={theme.colors.primary}>
                  {t("common.recipient")}
                </Subtitle>
                <IconButton
                  icon={<CrossIcon />}
                  color={theme.colors.white}
                  onClick={() => setOpen(false)}
                />
              </Row>

              <HorizontalSpacer spacing={1} />
              <Row nogutter justify="between" align="start">
                <div
                  style={{ display: "flex", gap: 4, flexDirection: "column" }}
                >
                  <FlexedDiv>
                    <Subheading>
                      {client?.name ?? clientIdentifier ?? t("common.unknown")}
                    </Subheading>
                    {getAmlStatusBadge(
                      client?.riskScore?.current?.totalScore,
                      client?.riskScore?.current?.riskScoreClass,
                      client?.riskScore?.currentManualRiskScore !== null,
                    )}
                  </FlexedDiv>
                  <BodySmall>
                    {t("common.organisationNumber")}:{" "}
                    {clientIdentifier ?? t("common.unknown")}
                  </BodySmall>
                  <HorizontalSpacer spacing={0.5} />
                  <ClientAddresses
                    clientIdentifier={clientIdentifier}
                    useTitles
                    useBackgroundColor={false}
                  />
                </div>
                <FlexedDiv style={{ gap: 24 }}>
                  {clientIdentifier && (
                    <OutlinedButton
                      title={t("invoiceAssessment.handleLimits")}
                      medium
                      color={theme.colors.primary}
                      icon={<FixIconSmall color={theme.colors.primary} />}
                      onClick={() =>
                        window.open(`/client/${clientIdentifier}`, "_blank")
                      }
                    />
                  )}
                  {clientIdentifier && (
                    <SendInvoiceMessageToClientButton
                      invoiceIds={[]}
                      clientIdentifier={clientIdentifier}
                      renderTrigger={(onClick) => (
                        <OutlinedButton
                          title={t("invoiceAssessment.contactRecipient")}
                          medium
                          color={theme.colors.primary}
                          icon={<SendIcon color={theme.colors.primary} />}
                          onClick={onClick}
                        />
                      )}
                    />
                  )}
                </FlexedDiv>
              </Row>

              <HorizontalSpacer spacing={1} />

              <Row>
                <Col lg={6}>
                  <OutlinedContainer>
                    <CreditScoreOverviewSmall
                      clientIdentifier={clientIdentifier}
                    />
                  </OutlinedContainer>
                </Col>
                <Col lg={6}>
                  <ClientOrgInfo clientIdentifier={clientIdentifier} />
                </Col>
              </Row>

              <HorizontalSpacer spacing={2} />
              <Row>
                <Col
                  lg={6}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 16,
                  }}
                >
                  <StatContainer>
                    <BodySmall>
                      {t("invoiceAssessment.externalLimit")}
                    </BodySmall>
                    <Body fontweight="600">
                      {formatPrice(
                        clientStatistics?.creditData?.creditRating
                          ?.creditLimit ?? 0,
                        0,
                      )}
                    </Body>
                  </StatContainer>
                  <StatContainer>
                    <BodySmall>
                      {t("invoiceAssessment.kfmDebtAmount")}
                    </BodySmall>
                    <Body fontweight="600">
                      {formatPrice(
                        clientStatistics?.creditData?.negativeInformation
                          ?.debtBalance?.totalBalance ?? 0,
                        0,
                      )}{" "}
                      {clientStatistics?.creditData?.negativeInformation
                        ?.debtBalance?.currency ?? ""}
                    </Body>
                  </StatContainer>
                  <StatContainer>
                    <BodySmall>
                      {t("invoiceAssessment.paymentDefaults")}
                    </BodySmall>
                    <Body fontweight="600">
                      {clientStatistics?.creditData?.negativeInformation
                        ?.recordOfNonPayment?.numberOfApplications ?? 0}
                    </Body>
                  </StatContainer>
                  <StatContainer>
                    <BodySmall>
                      {t("invoiceAssessment.overdueAmount")}
                    </BodySmall>
                    {overdueByCurrency.length > 0 ? (
                      overdueByCurrency.map((currency) => (
                        <Body key={currency.currencyCode} fontweight="600">
                          {`${formatAmountWithCurrency(currency.totalAmountIncludingVatOverdue, currency.currencyCode)} (${t("invoiceAssessment.overdueInvoiceCount", { count: currency.numberOfInvoicesOverdue })})`}
                        </Body>
                      ))
                    ) : (
                      <Body fontweight="600">-</Body>
                    )}
                  </StatContainer>
                  <StatContainer>
                    <BodySmall>
                      {t("invoiceAssessment.outstandingAmount")}
                    </BodySmall>
                    {overdueByCurrency.length > 0 ? (
                      overdueByCurrency.map((currency) => (
                        <Body key={currency.currencyCode} fontweight="600">
                          {formatAmountWithCurrency(
                            currency.totalAmountIncludingVatOutstanding,
                            currency.currencyCode,
                          )}
                        </Body>
                      ))
                    ) : (
                      <Body fontweight="600">-</Body>
                    )}
                  </StatContainer>
                </Col>
                <Col lg={6}>
                  <DonutStatSection
                    data={[
                      {
                        id: "1",
                        titleKey: "Använt utrymme",
                        percentage:
                          recipientTotal > 0
                            ? (recipientUsed / recipientTotal) * 100
                            : 0,
                        valueText: `${formatPrice(recipientUsed, 0)} kr`,
                      },
                      {
                        id: "2",
                        titleKey: "Återstående limit",
                        percentage:
                          recipientTotal > 0
                            ? (recipientRemaining / recipientTotal) * 100
                            : 0,
                        valueText: `${formatPrice(recipientRemaining, 0)} kr`,
                      },
                    ]}
                    colorType="midnight"
                    title={`Mottagarlimit - ${formatPrice(recipientTotal, 0)} kr`}
                    link="clientcard"
                  />
                </Col>
              </Row>

              <HorizontalSpacer spacing={2} />
              <OutlinedContainer>
                <OpenInvoicesList
                  invoices={openInvoices}
                  clientIdentifier={clientIdentifier}
                  currentPage={openInvoicesPage}
                  totalPages={Math.ceil(
                    (openInvoicesData?.totalCount ?? 0) / INVOICES_PER_PAGE,
                  )}
                  onPageChange={setOpenInvoicesPage}
                  selectedStatuses={openInvoicesStatuses}
                  onStatusChange={(statuses) => {
                    setOpenInvoicesStatuses(statuses);
                    setOpenInvoicesPage(0);
                  }}
                  searchTerm={openInvoicesSearch}
                  onSearchChange={(term) => {
                    setOpenInvoicesSearch(term);
                    setOpenInvoicesPage(0);
                  }}
                  sortBy={openInvoicesSortBy}
                  sortDirection={openInvoicesSortDirection}
                  onSortChange={(col, dir) => {
                    setOpenInvoicesSortBy(col);
                    setOpenInvoicesSortDirection(dir);
                    setOpenInvoicesPage(0);
                  }}
                />
              </OutlinedContainer>
            </OverlayContent>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
export default RecipientDetailsOverlay;
